import {
  completeLogin,
  completeTwoFactorLogin,
  prepareLogin,
  verifyLoginProof,
} from '@/crypto/key-hierarchy'
import type {
  LoginRequest,
  LoginResponse,
  PreloginResponse,
  TwoFactorLoginVerifyResponse,
} from '@/crypto/key-hierarchy.types'
import {
  loginVerifyTwoFactor as defaultLoginVerifyTwoFactor,
  requestTwoFactorEmailRecovery as defaultRequestEmailRecovery,
  verifyTwoFactorEmailRecovery as defaultVerifyEmailRecovery,
} from '../api/twoFactorApi'
import { login, prelogin } from '../api/authApi'
import { sessionStore, type UnlockedSnapshot } from './sessionStore'

/**
 * loginFlow — UNAUTH → UNLOCKED 遷移を担う orchestration layer (Phase 4 #44 Phase B 対応)。
 *
 * 2FA OFF: `loginWithMasterPassword(email, MP)` が完了すると `sessionStore.set` で
 * SK と material を保持して `{ kind: 'authenticated' }` を返す。
 *
 * 2FA ON: `login` が `twoFactorRequired:true` を返した場合は SK 復元を保留し、
 * SRP M2 だけ先に検証して `{ kind: 'two_factor_required', pending }` を返す。
 * caller (useLoginForm) は pending を保持し、ユーザーが 6 桁コードを入力したら
 * `completeTwoFactorLogin(pending, code)` を呼ぶ。失敗時の `pending.dispose()` で
 * `_smkEnc` を必ず zeroize すること (cancel / unmount / 5 分 TTL 切れ時)。
 *
 * material は prelogin response (kdf params + salt) と login response
 * (protectedSymmetricKey + iv) の両方から組み立てるため、本フロー層でしか正しく
 * 構築できない。`completeLogin` の戻り値型は変えず、本層で material を merge して
 * `set()` に渡す。
 */

export interface LoginFlowDeps {
  prelogin: (email: string) => Promise<PreloginResponse>
  login: (request: LoginRequest) => Promise<LoginResponse>
  loginVerifyTwoFactor: (
    code: string,
    preAuthToken: string,
    trustDevice?: boolean,
  ) => Promise<TwoFactorLoginVerifyResponse>
  /** 2fa-recovery: pre-auth token で email に 6 桁コードを送信させる */
  requestTwoFactorEmailRecovery: (preAuthToken: string) => Promise<void>
  /** 2fa-recovery: 6 桁コードを verify + login completion (TwoFactorLoginVerifyResponse と同型) */
  verifyTwoFactorEmailRecovery: (
    code: string,
    preAuthToken: string,
    trustDevice?: boolean,
  ) => Promise<TwoFactorLoginVerifyResponse>
  setSession: (session: UnlockedSnapshot) => void
}

const defaultDeps: LoginFlowDeps = {
  prelogin,
  login,
  loginVerifyTwoFactor: defaultLoginVerifyTwoFactor,
  requestTwoFactorEmailRecovery: defaultRequestEmailRecovery,
  verifyTwoFactorEmailRecovery: defaultVerifyEmailRecovery,
  setSession: session => sessionStore.set(session),
}

/**
 * 2FA pending 状態で保留される情報。caller は `submit()` か `dispose()` のいずれかを
 * 必ず呼ぶこと (`dispose()` が `_smkEnc` の zeroize 責任を負う)。
 */
export interface TwoFactorPendingHandle {
  readonly preAuthToken: string
  /** 6 桁 TOTP / recovery code 成功時に sessionStore を更新し、`authenticated` を返す */
  submit: (code: string) => Promise<void>
  /** email recovery: pre-auth で 6 桁 一時コードを email に送る (本人確認済前提) */
  requestEmailRecovery: () => Promise<void>
  /** email recovery: メール受信した 6 桁 code を verify + login completion */
  submitEmailRecovery: (code: string) => Promise<void>
  /** cancel / unmount / TTL 切れ時に `_smkEnc` を zeroize する */
  dispose: () => void
}

export type LoginFlowResult =
  | { kind: 'authenticated' }
  | { kind: 'two_factor_required'; pending: TwoFactorPendingHandle }

export const loginWithMasterPassword = async (
  email: string,
  password: string,
  options: { trustDevice?: boolean } = {},
  deps: LoginFlowDeps = defaultDeps,
): Promise<LoginFlowResult> => {
  const normalizedEmail = email.trim().toLowerCase()
  const preloginResponse = await deps.prelogin(normalizedEmail)
  const ctx = await prepareLogin(normalizedEmail, password, preloginResponse)

  let consumed = false

  const zeroizeCtx = (): void => {
    if (!consumed) {
      consumed = true
      ctx._smkEnc.fill(0)
    }
  }

  // "このデバイスを信頼" の値。明示指定がない場合は body に含めず
  // (サーバ側で `true` 扱い、後方互換)。指定があった場合のみ wire に載せる。
  const { trustDevice } = options

  const loginRequest: LoginRequest =
    trustDevice === undefined ? ctx.request : { ...ctx.request, trustDevice }

  try {
    const loginResponse = await deps.login(loginRequest)

    // M2 検証は両 path で必須。失敗で SrpVerificationError → catch で zeroize 済。
    verifyLoginProof(ctx, loginResponse.serverSessionProof)

    if (loginResponse.twoFactorRequired === true) {
      // 2FA pending: SK 復元を保留し、ctx を pending handle に閉じ込める。
      // trustDevice は SRP login 段階の選択値を閉包に閉じ込めて 2FA verify でも再送する
      // (UI 上は 2FA 画面で再選択できないため、login 画面の値が最終値となる)。
      // 共通の verify-and-complete ロジック (TOTP / email-recovery で同じ shape)
      const completeWithVerifyRes = async (
        verifyRes: TwoFactorLoginVerifyResponse,
      ): Promise<void> => {
        consumed = true

        const session = await completeTwoFactorLogin(ctx, verifyRes)

        deps.setSession({
          accessToken: session.accessToken,
          symmetricKey: session.symmetricKey,
          material: {
            kdfSalt: preloginResponse.kdf.salt,
            kdfParams: {
              memoryCost: preloginResponse.kdf.memoryCost,
              timeCost: preloginResponse.kdf.timeCost,
              parallelism: preloginResponse.kdf.parallelism,
            },
            protectedSymmetricKey: verifyRes.protectedSymmetricKey,
            protectedSymmetricKeyIv: verifyRes.protectedSymmetricKeyIv,
            email: normalizedEmail,
          },
        })
      }

      const pending: TwoFactorPendingHandle = {
        preAuthToken: loginResponse.twoFactorPreAuthToken,
        submit: async (code: string): Promise<void> => {
          if (consumed) throw new Error('two-factor pending already consumed')

          const verifyRes = await deps.loginVerifyTwoFactor(
            code,
            loginResponse.twoFactorPreAuthToken,
            trustDevice,
          )

          await completeWithVerifyRes(verifyRes)
        },
        requestEmailRecovery: async (): Promise<void> => {
          if (consumed) throw new Error('two-factor pending already consumed')

          await deps.requestTwoFactorEmailRecovery(loginResponse.twoFactorPreAuthToken)
        },
        submitEmailRecovery: async (code: string): Promise<void> => {
          if (consumed) throw new Error('two-factor pending already consumed')

          const verifyRes = await deps.verifyTwoFactorEmailRecovery(
            code,
            loginResponse.twoFactorPreAuthToken,
            trustDevice,
          )

          await completeWithVerifyRes(verifyRes)
        },
        dispose: zeroizeCtx,
      }

      return { kind: 'two_factor_required', pending }
    }

    // 通常 path: SK 復元 + session set (completeLogin 内で zeroize される)
    consumed = true

    const session = await completeLogin(ctx, loginResponse)

    deps.setSession({
      accessToken: session.accessToken,
      symmetricKey: session.symmetricKey,
      material: {
        kdfSalt: preloginResponse.kdf.salt,
        kdfParams: {
          memoryCost: preloginResponse.kdf.memoryCost,
          timeCost: preloginResponse.kdf.timeCost,
          parallelism: preloginResponse.kdf.parallelism,
        },
        protectedSymmetricKey: loginResponse.protectedSymmetricKey,
        protectedSymmetricKeyIv: loginResponse.protectedSymmetricKeyIv,
        email: normalizedEmail,
      },
    })

    return { kind: 'authenticated' }
  } catch (e) {
    zeroizeCtx()
    throw e
  }
}
